import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const WARN_SECONDS = 120;                 // Vorwarnung: 2 Minuten
const ACTIVITY_KEY = "clubops.lastActivity"; // über mehrere Tabs geteilt
const EVENTS = ["mousedown", "keydown", "wheel", "touchstart", "pointerdown"];

const readLast = () => Number(window.localStorage.getItem(ACTIVITY_KEY)) || Date.now();

/** Automatische Abmeldung nach Inaktivität. Die Dauer kommt pro Mandant aus den Einstellungen
 *  (`idle_logout_minutes`, 0 = deaktiviert); 2 Minuten vorher erscheint eine Warnung mit Countdown.
 *  Während die Warnung sichtbar ist, verlängert Mausbewegung/Tippen die Sitzung NICHT automatisch —
 *  der Nutzer muss aktiv bestätigen, damit eine unbeaufsichtigte Sitzung wirklich endet. */
export default function IdleLogout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const minutes = Number(user?.idle_logout_minutes ?? 0);
  const [left, setLeft] = useState(null); // Sekunden bis zur Abmeldung (null = keine Warnung)
  const warning = useRef(false);
  const loggingOut = useRef(false);

  const stayLoggedIn = useCallback(() => {
    window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    warning.current = false;
    setLeft(null);
  }, []);

  useEffect(() => {
    if (!user || minutes <= 0) { warning.current = false; setLeft(null); return; }
    const limit = minutes * 60 * 1000;
    window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));

    const onActivity = () => {
      if (warning.current) return; // Warnung nur per Klick auf "Angemeldet bleiben" beenden
      window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = setInterval(() => {
      const remaining = Math.ceil((limit - (Date.now() - readLast())) / 1000);
      if (remaining <= 0) {
        if (loggingOut.current) return;
        loggingOut.current = true;
        window.localStorage.removeItem(ACTIVITY_KEY);
        logout("idle"); // Hinweis "Automatisch abgemeldet" auf der Loginseite
      } else if (remaining <= WARN_SECONDS) {
        warning.current = true;
        setLeft(remaining);
      } else if (warning.current) {
        // Aktivität in einem anderen Tab hat die Sitzung verlängert.
        warning.current = false;
        setLeft(null);
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, minutes]);

  if (!user || minutes <= 0 || left === null) return null;

  const ss = String(left % 60).padStart(2, "0");

  return (
    <Dialog open>
      <DialogContent className="max-w-md [&>button]:hidden" data-testid="idle-warning"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>{t("idle.title")}</DialogTitle></DialogHeader>
        <DialogDescription>{t("idle.hint", { minutes })}</DialogDescription>
        <p className="text-center font-mono text-3xl font-semibold tabular-nums" data-testid="idle-countdown">
          {Math.floor(left / 60)}:{ss}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => logout("idle")} data-testid="idle-logout-now">
            {t("idle.logoutNow")}
          </Button>
          <Button onClick={stayLoggedIn} data-testid="idle-stay">{t("idle.stay")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
